import "./app.css";
import * as Sentry from "@sentry/react";
import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";
import {
	QueryClient,
	QueryClientProvider,
	type QueryKey,
} from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { persistQueryClient } from "@tanstack/react-query-persist-client";
import { useEffect } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Login } from "./components/Login";
import { ProjectTable } from "./components/ProjectTable";
import { WithSpinner } from "./components/WithQuery";
import { useUser } from "./hooks/useUser";

declare global {
	interface ImportMetaEnv {
		readonly MODE: string;
		readonly VITE_SENTRY_DSN: string;
		readonly VITE_SENTRY_RELEASE?: string;
		readonly VITE_BASE_URL: string;
	}

	interface ImportMeta {
		readonly env: ImportMetaEnv;
	}
}

const prod = import.meta.env.MODE === "production";
const sentryDSN = import.meta.env.VITE_SENTRY_DSN;
const sentryRelease = import.meta.env.VITE_SENTRY_RELEASE;

Sentry.init({
	dsn: sentryDSN,
	environment: prod ? "production" : "local",
	release: sentryRelease,
	tracesSampleRate: prod ? 1 : 0.5,
	integrations: [Sentry.browserTracingIntegration()],
	sendDefaultPii: true,
	debug: !prod,
});

const localStoragePersister = createAsyncStoragePersister({
	storage: window.localStorage,
});

export { localStoragePersister };

const doNotPersistQueries: QueryKey[] = [["entries-by-date"]];

export const cacheTime = 3600000;

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			gcTime: cacheTime, // changed from cacheTime to gcTime
			refetchOnWindowFocus: false,
		},
	},
});

// Check if we should skip cache restoration
const skipCacheRestore = localStorage.getItem("skip-cache-restore") === "true";
if (skipCacheRestore) {
	// Clear the flag so next time we do restore
	localStorage.removeItem("skip-cache-restore");
	// Remove the cache key before persistence is set up
	localStorage.removeItem("REACT_QUERY_OFFLINE_CACHE");
}

// Only persist if we're not skipping cache
if (!skipCacheRestore) {
	persistQueryClient({
		queryClient,
		persister: localStoragePersister,
		maxAge: cacheTime,
		hydrateOptions: {},
		dehydrateOptions: {
			shouldDehydrateQuery: ({ queryKey }: { queryKey: QueryKey }) => {
				return !doNotPersistQueries.includes(queryKey);
			},
		},
	});
}

const Home = () => {
	const userQuery = useUser();

	useEffect(() => {
		if (userQuery.data == null) {
			Sentry.setUser(null);
			return;
		}

		Sentry.setUser({
			id: userQuery.data?.id,
			username: userQuery.data?.name,
		});
	}, [userQuery.data]);

	if (userQuery.data == null) {
		return <WithSpinner loading={true}>{null}</WithSpinner>;
	}

	return <ProjectTable user={userQuery.data} />;
};

function App() {
	return (
		<Sentry.ErrorBoundary fallback={<p>An error has occurred</p>}>
			<QueryClientProvider client={queryClient}>
				<ReactQueryDevtools initialIsOpen={false} />
				<BrowserRouter>
					<Routes>
						<Route element={<Login />} path="/auth/login/" />
						<Route element={<Home />} path="/" />
					</Routes>
				</BrowserRouter>
			</QueryClientProvider>
		</Sentry.ErrorBoundary>
	);
}

export default App;
